import React from 'react'
import { Keyboard, TextInput } from 'react-native'
import { Avatar } from 'react-native-paper'

import { DS } from '../../design-system'
import { useStore } from '../../models'
import { events } from '../../models/analytics/analytics-events'
import { color, fontFamily } from '../../theme'
import { GradientButton } from '../GradientButton/GradientButton'
import { InspirationSheetType } from './DailyInspirationCommentSheet'
import { LoadingContent } from './LoadingContent'
import { styles } from './styles'

const Max_Size = 750

interface DefaultSheetContent {
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
  inspirationMessage: string
  userInitial: string
  userResponses: string
  setUserResponses: (text: string) => void
  onClose: () => void
  onSubmit: (userResponses: string) => void
  setIsModalVisible: (isModalVisible: boolean) => void
  setIsResponseSubmitted: (isResponseSubmitted: boolean) => void
  editResponseDataId?: string
  editResponseDataMsg?: string
  isReponseLoading?: boolean
  setIsReponseLoading?: (isReponseLoading: boolean) => void
  saveEditResponse?: () => void
  type: InspirationSheetType
  setIsDeletingResponse?: (isDeletingResponse: boolean) => void
}

export const DefaultSheetContent = (props: DefaultSheetContent) => {
  const {
    inspirationMessage,
    userInitial,
    userResponses,
    setUserResponses,
    onClose,
    onSubmit,
    editResponseDataId = '',
    editResponseDataMsg,
    isReponseLoading,
    saveEditResponse,
    type,
    setIsDeletingResponse
  } = props

  const {
    session: { user },
    analytics: { setEvent }
  } = useStore()
  const userId = user?._id.toString()

  const onDelete = () => {
    Keyboard.dismiss()
    setTimeout(() => {
      if (setIsDeletingResponse) setIsDeletingResponse(true)
    }, 100)
  }

  const onCancel = () => {
    Keyboard.dismiss()
    setTimeout(() => {
      setEvent(events.dailyInspirationEvents.response_canceled, {
        userId: userId
      })
      setUserResponses('')
      onClose()
    }, 100)
  }

  const onSaveChanges = () => {
    Keyboard.dismiss()
    setTimeout(() => {
      onClose()
      if (saveEditResponse) saveEditResponse()
    }, 100)
  }

  return (
    <React.Fragment key={'DEFAULT'}>
      {isReponseLoading ? (
        <LoadingContent />
      ) : (
        <React.Fragment key={'user-response-input'}>
          <DS.Text variant="Body Small/Regular" textAlign="left" style={{ marginTop: 8 }}>
            {inspirationMessage}
          </DS.Text>
          <DS.Frame row gap="8" alignItems="flex-start" justifyContent="flex-start" style={{ marginTop: 16 }}>
            <Avatar.Text
              size={32}
              label={userInitial}
              color={color.palette.black}
              style={{ backgroundColor: color.palette.pink }}
              labelStyle={{ fontFamily: fontFamily.GTAmericaExtended, fontSize: 12 }}
            />
            <TextInput
              autoFocus={true}
              placeholder="Start writing..."
              multiline={true}
              maxLength={750}
              onChangeText={(text) => {
                const trimmedText = text.trim()
                setUserResponses(trimmedText)
              }}
              defaultValue={editResponseDataMsg}
              placeholderTextColor={DS.palette['Brand/Dark Grey']}
              style={styles.userResponseInput}
            />
          </DS.Frame>
          <DS.Frame style={styles.maxSizeView}>
            <DS.Text
              variant="Mini/Medium"
              color={userResponses.length >= Max_Size ? 'Utility/Warning' : 'Global/Black'}
              textAlign="right"
            >
              {userResponses.length}/{Max_Size}
            </DS.Text>
          </DS.Frame>
          {type === InspirationSheetType.DEFAULT ? (
            <DS.Frame row gap="8">
              <DS.Button
                variant={'Large/Primary'}
                type={'On Dark'}
                backgroundColor={'Brand/Cream'}
                borderColor={'Brand/Light Grey'}
                style={styles.cancelButton}
                onPress={onCancel}
              >
                <DS.Text variant="Body Medium/Medium" color="Global/Black" textAlign="center">
                  Cancel
                </DS.Text>
              </DS.Button>
              {userResponses.length < 11 ? (
                <DS.Button
                  disabled={true}
                  variant={'Large/Primary'}
                  type={'On Dark'}
                  backgroundColor={'Brand/Dark Grey'}
                  borderColor={'Brand/Light Grey'}
                  style={styles.submitGradientButton}
                >
                  <DS.Text variant="Body Medium/Medium" color="Global/White" textAlign="center">
                    Submit
                  </DS.Text>
                </DS.Button>
              ) : (
                <GradientButton
                  title="Submit"
                  onPress={() => {
                    Keyboard.dismiss()
                    onSubmit(userResponses)
                  }}
                  contentContainerStyle={styles.submitGradientButton}
                />
              )}
            </DS.Frame>
          ) : (
            <DS.Frame row gap="8">
              <DS.Button
                variant={'Large/Primary'}
                type={'On Dark'}
                backgroundColor={'Brand/Cream'}
                borderColor={'Brand/Light Grey'}
                style={styles.deleteButton}
                onPress={onDelete}
              >
                <DS.Icon name="Trash" size={24} color={'Global/Black'} onPress={onDelete} />
              </DS.Button>
              <DS.Button
                variant={'Large/Primary'}
                type={'On Dark'}
                backgroundColor={'Brand/Cream'}
                borderColor={'Brand/Light Grey'}
                style={[styles.cancelButton]}
                onPress={onCancel}
              >
                <DS.Text variant="Body Medium/Medium" color="Global/Black" textAlign="center">
                  Cancel
                </DS.Text>
              </DS.Button>
              {userResponses.length < 11 ? (
                <DS.Button
                  disabled={true}
                  variant={'Large/Primary'}
                  type={'On Dark'}
                  backgroundColor={'Brand/Dark Grey'}
                  borderColor={'Brand/Light Grey'}
                  style={[styles.submitGradientButton, { width: '52%' }]}
                >
                  <DS.Text variant="Body Medium/Medium" color="Global/White" textAlign="center">
                    Save Changes
                  </DS.Text>
                </DS.Button>
              ) : (
                <GradientButton title="Save Changes" onPress={onSaveChanges} contentContainerStyle={{ width: '52%' }} />
              )}
            </DS.Frame>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  )
}
